import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../../components/redux/hooks/useOnboarding';


const ConnectEHRModal = ({ isOpen, onClose, onComplete, onAssignToIT, onDoLater }) => {
  const navigate = useNavigate();
  const { setEhrCredentialsState, ehrCredentials: savedEhrCredentials } = useOnboarding();
  const lastSavedCredentialsRef = useRef(null);
  const [steps, setSteps] = useState([
    { id: 1, title: 'Step 1: Locate your EHR API credentials', completed: false, expanded: false },
    { id: 2, title: 'Step 2: Configure data sync preferences', completed: false, expanded: false },
    { id: 3, title: 'Step 3: Test the connection', completed: false, expanded: false },
    { id: 4, title: 'Step 4: Activate integration', completed: false, expanded: false },
  ]);

  // Step 1: EHR Credentials form state - initialize from Redux if available
  const [ehrCredentials, setEhrCredentials] = useState({
    clientId: savedEhrCredentials?.clientId || '',
    clientSecret: savedEhrCredentials?.clientSecret || '',
    practiceId: savedEhrCredentials?.practiceId || '',
    baseUrl: savedEhrCredentials?.baseUrl || ''
  });
  const [ehrErrors, setEhrErrors] = useState({});
  const [ehrTouched, setEhrTouched] = useState({});

  // Validation functions
  const validateClientId = (value) => {
    if (!value.trim()) return 'Client ID is required';
    // if (value.trim().length < 8) return 'Client ID must be at least 8 characters';
    return '';
  };

  const validateClientSecret = (value) => {
    if (!value.trim()) return 'Client Secret is required';
    // if (value.trim().length < 16) return 'Client Secret must be at least 16 characters';
    return '';
  };

  const validatePracticeId = (value) => {
    if (!value.trim()) return 'Practice ID is required';
    if (!/^[a-zA-Z0-9-]+$/.test(value.trim())) return 'Practice ID can only contain letters, numbers, and hyphens';
    return '';
  };

  const validateBaseUrl = (value) => {
    if (!value.trim()) return 'Base URL is required';
    try {
      const url = new URL(value.trim());
      if (!['http:', 'https:'].includes(url.protocol)) {
        return 'Base URL must start with http:// or https://';
      }
    } catch {
      return 'Please enter a valid URL';
    }
    return '';
  };

  // Validate all Step 1 fields
  const validateStep1 = useCallback(() => {
    const errors = {
      clientId: validateClientId(ehrCredentials.clientId),
      clientSecret: validateClientSecret(ehrCredentials.clientSecret),
      practiceId: validatePracticeId(ehrCredentials.practiceId),
      baseUrl: validateBaseUrl(ehrCredentials.baseUrl)
    };
    return errors;
  }, [ehrCredentials]);

  // Check if Step 1 is valid
  const isStep1Valid = useCallback(() => {
    const errors = validateStep1();
    return Object.values(errors).every(error => error === '');
  }, [validateStep1]);

  // Auto-check Step 1 when all validations pass and save to Redux
  useEffect(() => {
    const allFieldsFilled = ehrCredentials.clientId && ehrCredentials.clientSecret && 
                            ehrCredentials.practiceId && ehrCredentials.baseUrl;
    if (allFieldsFilled && isStep1Valid()) {
      setSteps(prev => prev.map(step => 
        step.id === 1 ? { ...step, completed: true } : step
      ));
      // Save EHR credentials to Redux only if they changed
      const credentialsKey = JSON.stringify(ehrCredentials);
      if (lastSavedCredentialsRef.current !== credentialsKey) {
        lastSavedCredentialsRef.current = credentialsKey;
        setEhrCredentialsState({
          clientId: ehrCredentials.clientId,
          clientSecret: ehrCredentials.clientSecret,
          practiceId: ehrCredentials.practiceId,
          baseUrl: ehrCredentials.baseUrl
        });
      }
    } else {
      setSteps(prev => prev.map(step => 
        step.id === 1 ? { ...step, completed: false } : step
      ));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ehrCredentials, isStep1Valid]);

  // Handle input change for Step 1
  const handleEhrInputChange = (field, value) => {
    setEhrCredentials(prev => ({ ...prev, [field]: value }));
    
    // Validate on change if field was touched
    if (ehrTouched[field]) {
      const validators = {
        clientId: validateClientId,
        clientSecret: validateClientSecret,
        practiceId: validatePracticeId,
        baseUrl: validateBaseUrl
      };
      setEhrErrors(prev => ({ ...prev, [field]: validators[field](value) }));
    }
  };

  // Handle input blur for Step 1
  const handleEhrInputBlur = (field) => {
    setEhrTouched(prev => ({ ...prev, [field]: true }));
    const validators = {
      clientId: validateClientId,
      clientSecret: validateClientSecret,
      practiceId: validatePracticeId,
      baseUrl: validateBaseUrl
    };
    setEhrErrors(prev => ({ ...prev, [field]: validators[field](ehrCredentials[field]) }));
  };

  const completedCount = steps.filter(s => s.completed).length;
  const progressPercentage = (completedCount / steps.length) * 100;

  const toggleStep = (stepId) => {
    setSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, expanded: !step.expanded } : step
    ));
  };

  const toggleStepComplete = (stepId) => {
    // Only allow manual toggle for steps other than Step 1
    if (stepId === 1) return;
    setSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, completed: !step.completed } : step
    ));
  };

  const handleCompleteSetup = () => {
    onComplete?.();
    onClose?.();
    navigate('/onboarding/ehr-connect');
  };

  const handleDoLater = () => {
    onDoLater?.();
    onClose?.();
    navigate('/onboarding/ehr-connect');
  };

  const handleAssignToIT = () => {
    onAssignToIT?.();
  };

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000 }} onClick={onClose}>
      <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content border-0 rounded-4" style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 800, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-start p-4 text-white" style={{ 
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', 
            padding: '20px 24px', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start' 
          }}>
            <div className="d-flex align-items-center gap-3" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className="d-flex align-items-center justify-content-center rounded-3" style={{ 
                width: 40, 
                height: 40, 
                background: 'rgba(255, 255, 255, 0.2)', 
                borderRadius: 8, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: 20 
              }}>
                <span>📊</span>
              </div>
              <h2 className="h5 mb-0" style={{ color: '#fff', fontSize: '20px', fontWeight: 600, margin: 0 }}>Connect your EHR</h2>
            </div>
            <button 
              className="btn btn-link text-white p-0" 
              style={{ 
                background: 'none', 
                border: 'none', 
                color: 'rgba(255, 255, 255, 0.8)', 
                fontSize: 24, 
                cursor: 'pointer', 
                padding: 0, 
                transition: 'color 0.2s' 
              }}
              onClick={onClose}
              onMouseEnter={(e) => e.target.style.color = '#fff'}
              onMouseLeave={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.8)'}
            >
              ✕
            </button>
          </div>

          {/* Badges */}
          <div className="d-flex gap-3 px-4 pb-4 text-white" style={{ 
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', 
            padding: '0 24px 20px', 
            display: 'flex', 
            gap: '12px' 
          }}>
            <span className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-2 small" style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '6px', 
              padding: '4px 8px', 
              borderRadius: 8, 
              background: 'rgba(255, 255, 255, 0.2)', 
              color: '#fff', 
              fontSize: 12, 
              fontWeight: 500 
            }}>
              <span className="small" style={{ fontSize: 14 }}>⏱️</span> 5 minutes
            </span>
            <span className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-2 small" style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '6px', 
              padding: '4px 8px', 
              borderRadius: 8, 
              background: 'rgba(255, 255, 255, 0.2)', 
              color: '#fbbf24', 
              fontSize: 12, 
              fontWeight: 500 
            }}>
              <span className="small" style={{ fontSize: 14 }}>⭐</span> +50 XP
            </span>
          </div>

          {/* Modal Body */}
          <div className="p-4" style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
            {/* Why this matters section */}
            <div className="d-flex gap-3 p-3 bg-light rounded-3 mb-4" style={{ 
              display: 'flex', 
              gap: '12px', 
              padding: '16px', 
              background: '#eff6ff', 
              border: '1px solid #dbeafe', 
              borderRadius: 12, 
              marginBottom: '20px' 
            }}>
              <div className="flex-shrink-0" style={{ fontSize: 20, flexShrink: 0 }}>ℹ️</div>
              <div className="flex-grow-1">
                <h4 className="h6 fw-bold text-primary mb-1" style={{ 
                  fontSize: '15px', 
                  fontWeight: 600, 
                  color: '#1e40af', 
                  margin: '0 0 6px 0' 
                }}>Why this matters</h4>
                <p className="text-muted mb-0 small" style={{ 
                  fontSize: '13px', 
                  color: '#4b5563', 
                  margin: 0, 
                  lineHeight: 1.5 
                }}>
                  Connecting your EHR system enables seamless patient data flow, reduces manual entry errors, 
                  and saves your team up to 15 hours per week on administrative tasks.
                </p>
              </div>
            </div>

            {/* EHR Preview Image */}
            <div className="mb-4 rounded-3 overflow-hidden border" style={{ 
              marginBottom: '24px', 
              borderRadius: 12, 
              overflow: 'hidden', 
              border: '1px solid #e5e7eb' 
            }}>
              <div className="p-3" style={{ 
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #1e293b 50%)', 
                padding: 16 
              }}>
                <div className="d-flex align-items-center gap-3 mb-3" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '16px', 
                  marginBottom: '12px' 
                }}>
                  <div className="px-2 py-1 bg-white rounded-1 small" style={{ 
                    background: '#fff', 
                    padding: '6px 12px', 
                    borderRadius: 6, 
                    fontSize: 11, 
                    fontWeight: 600, 
                    color: '#1e293b' 
                  }}>🏥 HealthCare</div>
                  <div className="text-white small" style={{ 
                    color: '#fff', 
                    fontSize: '13px', 
                    fontWeight: 500 
                  }}>Dash window EHR</div>
                </div>
                <div className="d-flex gap-2" style={{ 
                  display: 'flex', 
                  gap: '12px', 
                  background: '#1e293b', 
                  borderRadius: 8, 
                  padding: 8, 
                  minHeight: 120 
                }}>
                  <div className="d-flex flex-column gap-1" style={{ 
                    width: 80, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '4px' 
                  }}>
                    <div className="text-muted text-center py-1 rounded" style={{ 
                      fontSize: 10, 
                      color: '#94a3b8', 
                      padding: '4px 8px', 
                      borderRadius: 4, 
                      cursor: 'pointer' 
                    }} onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                       onMouseLeave={(e) => e.target.style.background = 'transparent'}>📋 Home</div>
                    <div className="text-muted text-center py-1 rounded" style={{ 
                      fontSize: 10, 
                      color: '#94a3b8', 
                      padding: '4px 8px', 
                      borderRadius: 4, 
                      cursor: 'pointer' 
                    }} onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                       onMouseLeave={(e) => e.target.style.background = 'transparent'}>📊 Charts</div>
                    <div className="text-muted text-center py-1 rounded" style={{ 
                      fontSize: 10, 
                      color: '#94a3b8', 
                      padding: '4px 8px', 
                      borderRadius: 4, 
                      cursor: 'pointer' 
                    }} onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                       onMouseLeave={(e) => e.target.style.background = 'transparent'}>📅 Calendar</div>
                    <div className="text-muted text-center py-1 rounded" style={{ 
                      fontSize: 10, 
                      color: '#94a3b8', 
                      padding: '4px 8px', 
                      borderRadius: 4, 
                      cursor: 'pointer' 
                    }} onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                       onMouseLeave={(e) => e.target.style.background = 'transparent'}>📁 Pages</div>
                    <div className="text-muted text-center py-1 rounded" style={{ 
                      fontSize: 10, 
                      color: '#94a3b8', 
                      padding: '4px 8px', 
                      borderRadius: 4, 
                      cursor: 'pointer' 
                    }} onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                       onMouseLeave={(e) => e.target.style.background = 'transparent'}>📈 Preset</div>
                    <div className="text-muted text-center py-1 rounded" style={{ 
                      fontSize: 10, 
                      color: '#94a3b8', 
                      padding: '4px 8px', 
                      borderRadius: 4, 
                      cursor: 'pointer' 
                    }} onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                       onMouseLeave={(e) => e.target.style.background = 'transparent'}>📉 Scope</div>
                    <div className="text-muted text-center py-1 rounded" style={{ 
                      fontSize: 10, 
                      color: '#94a3b8', 
                      padding: '4px 8px', 
                      borderRadius: 4, 
                      cursor: 'pointer' 
                    }} onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                       onMouseLeave={(e) => e.target.style.background = 'transparent'}>⚙️ Extra</div>
                  </div>
                  <div className="flex-grow-1 d-flex flex-column gap-2" style={{ 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '8px' 
                  }}>
                    <div className="d-flex gap-2" style={{ display: 'flex', gap: '8px' }}>
                      <div className="px-3 py-1 bg-danger text-white rounded-2 small" style={{ 
                        padding: '8px 16px', 
                        borderRadius: 6, 
                        fontSize: 11, 
                        fontWeight: 500, 
                        color: '#fff', 
                        background: '#ef4444' 
                      }}>Realtime</div>
                      <div className="px-3 py-1 bg-success text-white rounded-2 small" style={{ 
                        padding: '8px 16px', 
                        borderRadius: 6, 
                        fontSize: 11, 
                        fontWeight: 500, 
                        color: '#fff', 
                        background: '#10b981' 
                      }}>✓</div>
                      <div className="px-3 py-1 bg-primary text-white rounded-2 small" style={{ 
                        padding: '8px 16px', 
                        borderRadius: 6, 
                        fontSize: 11, 
                        fontWeight: 500, 
                        color: '#fff', 
                        background: '#3b82f6' 
                      }}>Notify</div>
                    </div>
                    <div className="d-flex gap-3 border-bottom pb-1" style={{ 
                      display: 'flex', 
                      gap: '16px', 
                      fontSize: 11, 
                      color: '#94a3b8', 
                      borderBottom: '1px solid #334155', 
                      paddingBottom: '4px' 
                    }}>
                      <span>EHR Validate</span>
                      <span>Q. Option</span>
                    </div>
                    <div className="flex-grow-1 d-flex flex-column gap-1" style={{ 
                      flex: 1, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '4px' 
                    }}>
                      <div className="bg-secondary rounded" style={{ 
                        height: 12, 
                        background: '#334155', 
                        borderRadius: 2 
                      }}></div>
                      <div className="bg-secondary rounded" style={{ 
                        height: 12, 
                        background: '#334155', 
                        borderRadius: 2 
                      }}></div>
                      <div className="bg-secondary rounded" style={{ 
                        height: 12, 
                        background: '#334155', 
                        borderRadius: 2 
                      }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Setup Steps */}
            <div className="mb-4" style={{ marginBottom: '20px' }}>
              <div className="d-flex align-items-center gap-2 mb-3" style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                marginBottom: '16px' 
              }}>
                <span className="fs-5" style={{ fontSize: 18 }}>📋</span>
                <h3 className="h6 mb-0" style={{ 
                  fontSize: '16px', 
                  fontWeight: 600, 
                  color: '#1a1a2e', 
                  margin: 0 
                }}>Setup Steps</h3>
              </div>

              <div className="d-flex flex-column gap-2" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {steps.map((step) => (
                  <div key={step.id} className={`border rounded-3 overflow-hidden ${step.completed ? 'bg-success bg-opacity-10 border-success-subtle' : ''}`} style={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: 10, 
                    overflow: 'hidden', 
                    display: 'block !important',
                    ...(step.completed && {
                      background: '#f0fdf4',
                      borderColor: '#bbf7d0'
                    })
                  }} onMouseEnter={(e) => {
                    if (!step.completed) {
                      e.target.style.borderColor = '#c7d2fe';
                    }
                  }} onMouseLeave={(e) => {
                    if (!step.completed) {
                      e.target.style.borderColor = '#e5e7eb';
                    }
                  }}>
                    <div className="d-flex align-items-center p-3 cursor-pointer" style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'flex-start', 
                      padding: '12px 16px', 
                      cursor: 'pointer' 
                    }} onClick={() => toggleStep(step.id)}>
                      <label className="position-relative d-flex align-items-center me-3 cursor-pointer" style={{ 
                        position: 'relative', 
                        display: 'flex', 
                        alignItems: 'center', 
                        cursor: 'pointer' 
                      }} onClick={(e) => e.stopPropagation()}>
                        <input 
                          type="checkbox" 
                          className="position-absolute opacity-0" 
                          style={{ 
                            position: 'absolute', 
                            opacity: 0, 
                            cursor: 'pointer', 
                            height: 0, 
                            width: 0 
                          }}
                          checked={step.completed}
                          disabled={step.id === 1}
                          readOnly={step.id === 1}
                          onChange={() => step.id !== 1 && toggleStepComplete(step.id)}
                        />
                        <span className={`d-inline-block border-2 rounded-1 ${step.completed ? 'bg-success border-success' : 'border-secondary'}`} style={{ 
                          width: 20, 
                          height: 20, 
                          border: '2px solid #d1d5db', 
                          borderRadius: 4, 
                          backgroundColor: step.completed ? '#10b981' : 'transparent',
                          borderColor: step.completed ? '#10b981' : '#d1d5db',
                          transition: 'all 0.2s',
                          position: 'relative'
                        }}>
                          {step.completed && (
                            <span className="position-absolute top-50 start-50 translate-middle text-white small" style={{ 
                              color: '#fff', 
                              fontSize: 12, 
                              fontWeight: 600,
                              transform: 'translate(-50%, -50%)',
                              position: 'absolute',
                              top: '50%',
                              left: '50%'
                            }}>✓</span>
                          )}
                        </span>
                      </label>
                      <span className={`flex-grow-1 small ${step.completed ? 'text-success' : 'text-secondary'}`} style={{ 
                        flex: 1, 
                        fontSize: 14, 
                        color: step.completed ? '#059669' : '#374151', 
                        textAlign: 'left' 
                      }}>{step.title}</span>
                      <button 
                        className="btn btn-link p-0 text-secondary" 
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: '#9ca3af', 
                          fontSize: 16, 
                          cursor: 'pointer', 
                          padding: 0, 
                          transition: 'color 0.2s' 
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStep(step.id);
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#6366f1'}
                        onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                      >
                        {step.expanded ? '∧' : '∨'}
                      </button>
                    </div>
                    {step.expanded && (
                      <div className="px-4 pb-4 pt-0 small text-muted" style={{ 
                        padding: '0 16px 16px 48px', 
                        fontSize: 13, 
                        color: '#6b7280', 
                        lineHeight: 1.5 
                      }}>
                        {step.id === 1 ? (
                          <div className="d-flex flex-column gap-3" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div className="d-flex flex-column gap-1" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label htmlFor="clientId" className="small fw-semibold" style={{ 
                                fontSize: 13, 
                                fontWeight: 500, 
                                color: '#374151' 
                              }}>Client ID *</label>
                              <input
                                type="text"
                                id="clientId"
                                placeholder="Enter your EHR Client ID"
                                value={ehrCredentials.clientId}
                                onChange={(e) => handleEhrInputChange('clientId', e.target.value)}
                                onBlur={() => handleEhrInputBlur('clientId')}
                                className={`form-control ${ehrErrors.clientId && ehrTouched.clientId ? 'is-invalid' : ''}`}
                                style={{ 
                                  padding: '10px 12px', 
                                  border: ehrErrors.clientId && ehrTouched.clientId ? '1px solid #ef4444' : '1px solid #d1d5db', 
                                  borderRadius: 8, 
                                  fontSize: 14, 
                                  color: '#1f2937', 
                                  outline: 'none', 
                                  transition: 'all 0.2s' 
                                }}
                              />
                              {ehrErrors.clientId && ehrTouched.clientId && (
                                <span className="text-danger small" style={{ 
                                  fontSize: '0.875rem', 
                                  color: '#ef4444', 
                                  marginTop: '-0.25rem', 
                                  display: 'block' 
                                }}>{ehrErrors.clientId}</span>
                              )}
                            </div>
                            <div className="d-flex flex-column gap-1" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label htmlFor="clientSecret" className="small fw-semibold" style={{ 
                                fontSize: 13, 
                                fontWeight: 500, 
                                color: '#374151' 
                              }}>Client Secret *</label>
                              <input
                                type="password"
                                id="clientSecret"
                                placeholder="Enter your EHR Client Secret"
                                value={ehrCredentials.clientSecret}
                                onChange={(e) => handleEhrInputChange('clientSecret', e.target.value)}
                                onBlur={() => handleEhrInputBlur('clientSecret')}
                                className={`form-control ${ehrErrors.clientSecret && ehrTouched.clientSecret ? 'is-invalid' : ''}`}
                                style={{ 
                                  padding: '10px 12px', 
                                  border: ehrErrors.clientSecret && ehrTouched.clientSecret ? '1px solid #ef4444' : '1px solid #d1d5db', 
                                  borderRadius: 8, 
                                  fontSize: 14, 
                                  color: '#1f2937', 
                                  outline: 'none', 
                                  transition: 'all 0.2s' 
                                }}
                              />
                              {ehrErrors.clientSecret && ehrTouched.clientSecret && (
                                <span className="text-danger small" style={{ 
                                  fontSize: '0.875rem', 
                                  color: '#ef4444', 
                                  marginTop: '-0.25rem', 
                                  display: 'block' 
                                }}>{ehrErrors.clientSecret}</span>
                              )}
                            </div>
                            <div className="d-flex flex-column gap-1" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label htmlFor="practiceId" className="small fw-semibold" style={{ 
                                fontSize: 13, 
                                fontWeight: 500, 
                                color: '#374151' 
                              }}>Practice ID *</label>
                              <input
                                type="text"
                                id="practiceId"
                                placeholder="Enter your Practice ID"
                                value={ehrCredentials.practiceId}
                                onChange={(e) => handleEhrInputChange('practiceId', e.target.value)}
                                onBlur={() => handleEhrInputBlur('practiceId')}
                                className={`form-control ${ehrErrors.practiceId && ehrTouched.practiceId ? 'is-invalid' : ''}`}
                                style={{ 
                                  padding: '10px 12px', 
                                  border: ehrErrors.practiceId && ehrTouched.practiceId ? '1px solid #ef4444' : '1px solid #d1d5db', 
                                  borderRadius: 8, 
                                  fontSize: 14, 
                                  color: '#1f2937', 
                                  outline: 'none', 
                                  transition: 'all 0.2s' 
                                }}
                              />
                              {ehrErrors.practiceId && ehrTouched.practiceId && (
                                <span className="text-danger small" style={{ 
                                  fontSize: '0.875rem', 
                                  color: '#ef4444', 
                                  marginTop: '-0.25rem', 
                                  display: 'block' 
                                }}>{ehrErrors.practiceId}</span>
                              )}
                            </div>
                            <div className="d-flex flex-column gap-1" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label htmlFor="baseUrl" className="small fw-semibold" style={{ 
                                fontSize: 13, 
                                fontWeight: 500, 
                                color: '#374151' 
                              }}>Base URL *</label>
                              <input
                                type="url"
                                id="baseUrl"
                                placeholder="https://api.yourehr.com"
                                value={ehrCredentials.baseUrl}
                                onChange={(e) => handleEhrInputChange('baseUrl', e.target.value)}
                                onBlur={() => handleEhrInputBlur('baseUrl')}
                                className={`form-control ${ehrErrors.baseUrl && ehrTouched.baseUrl ? 'is-invalid' : ''}`}
                                style={{ 
                                  padding: '10px 12px', 
                                  border: ehrErrors.baseUrl && ehrTouched.baseUrl ? '1px solid #ef4444' : '1px solid #d1d5db', 
                                  borderRadius: 8, 
                                  fontSize: 14, 
                                  color: '#1f2937', 
                                  outline: 'none', 
                                  transition: 'all 0.2s' 
                                }}
                              />
                              {ehrErrors.baseUrl && ehrTouched.baseUrl && (
                                <span className="text-danger small" style={{ 
                                  fontSize: '0.875rem', 
                                  color: '#ef4444', 
                                  marginTop: '-0.25rem', 
                                  display: 'block' 
                                }}>{ehrErrors.baseUrl}</span>
                              )}
                            </div>
                            {isStep1Valid() && (
                              <div className="alert alert-success py-2 px-3 small" style={{ 
                                background: '#f0fdf4', 
                                color: '#059669', 
                                border: '1px solid #bbf7d0', 
                                borderRadius: 8, 
                                padding: '8px 12px', 
                                fontSize: 13, 
                                fontWeight: 500 
                              }}>
                                ✓ All credentials validated successfully
                              </div>
                            )}
                          </div>
                        ) : (
                          <p>Step {step.id} instructions will appear here...</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>

            {/* Progress */}
            <div className="bg-light p-3 rounded-3" style={{ 
              background: '#f9fafb', 
              borderRadius: 10, 
              padding: 16 
            }}>
              <div className="d-flex justify-content-between align-items-center mb-2" style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: 8 
              }}>
                <span className="small fw-semibold">Progress</span>
                <span className="small text-muted">{completedCount}/{steps.length} completed</span>
              </div>
              <div className="progress" style={{ 
                width: '100%', 
                height: 6, 
                backgroundColor: '#e5e7eb', 
                borderRadius: 4, 
                overflow: 'hidden' 
              }}>
                <div 
                  className="progress-bar" 
                  style={{ 
                    height: '100%', 
                    background: 'linear-gradient(90deg, #4f46e5 0%, #7c3aed 100%)', 
                    borderRadius: 2, 
                    width: `${progressPercentage}%` 
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="d-flex justify-content-between align-items-center p-4 border-top bg-light" style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '20px 24px', 
            borderTop: '1px solid #e5e7eb', 
            background: '#f9fafb' 
          }}>
            <button 
              className="btn btn-outline-secondary d-flex align-items-center gap-2" 
              style={{ 
                background: 'white', 
                color: '#6b7280', 
                border: '1px solid #d1d5db', 
                borderRadius: 8, 
                padding: '10px 16px', 
                fontSize: 14, 
                fontWeight: 500, 
                cursor: 'pointer', 
                transition: 'all 0.2s', 
                display: 'flex', 
                alignItems: 'center', 
                gap: 8 
              }}
              onClick={handleAssignToIT}
              onMouseEnter={(e) => {
                e.target.style.background = '#f3f4f6';
                e.target.style.borderColor = '#9ca3af';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'white';
                e.target.style.borderColor = '#d1d5db';
              }}
            >
              <span style={{ fontSize: 16 }}>👤</span>
              Assign to IT
            </button>
            <div className="d-flex gap-2" style={{ display: 'flex', gap: '8px' }}>
              <button 
                className="btn btn-light" 
                style={{ 
                  background: 'white', 
                  color: '#6b7280', 
                  border: '1px solid #d1d5db', 
                  borderRadius: 8, 
                  padding: '10px 16px', 
                  fontSize: 14, 
                  fontWeight: 500, 
                  cursor: 'pointer', 
                  transition: 'all 0.2s' 
                }}
                onClick={handleDoLater}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f3f4f6';
                  e.target.style.borderColor = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.borderColor = '#d1d5db';
                }}
              >
                I&apos;ll do this later
              </button>
              <button 
                className={`btn ${completedCount >= steps.length ? 'btn-primary' : 'btn-secondary'}`} 
                onClick={handleCompleteSetup}
                disabled={completedCount < steps.length}
                style={{ 
                  background: completedCount >= steps.length ? '#3b82f6' : '#d1d5db', 
                  color: completedCount >= steps.length ? 'white' : '#9ca3af', 
                  border: 'none', 
                  borderRadius: 8, 
                  padding: '10px 16px', 
                  fontSize: 14, 
                  fontWeight: 500, 
                  cursor: completedCount >= steps.length ? 'pointer' : 'not-allowed', 
                  transition: 'all 0.2s' 
                }}
                onMouseEnter={(e) => {
                  if (completedCount >= steps.length) {
                    e.target.style.background = '#2563eb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (completedCount >= steps.length) {
                    e.target.style.background = '#3b82f6';
                  }
                }}
              >
                Complete Setup →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectEHRModal;
